import { intlLocaleOf } from '@/i18n/runtime';
/**
 * Appointment-time rendering — ONE helper for every surface that shows a booked
 * slot to the customer.
 *
 * <p>This is the `stamp` that lived inside Schedule.tsx, lifted out unchanged
 * (same Intl options, same customer-timezone resolution) because the scheduled-
 * arrival screens must render the SAME appointment the booking screen rendered.
 * A second formatter would be a second truth: the customer would read one time
 * when they booked and a differently-shaped one when they arrived early, and any
 * disagreement about timezone would land on the one screen where they are
 * already anxious about being late.
 *
 * <p><strong>Two lines, deliberately.</strong> The slot is shown in the
 * customer's own timezone first — that is the time they must act on — and in IST
 * beneath it, because the officer roster, the tenant's support hours and every
 * reminder are IST (the completion screen already stamps IST for the same
 * reason). When the customer's own zone RENDERS IDENTICALLY to IST — the
 * overwhelming majority of journeys, and every `Asia/Calcutta`-style alias — the
 * second line is dropped rather than printing the same clock time twice under a
 * different label.
 */

/** The regulated home zone: officer rosters, reminders and audit stamps are IST. */
export const IST_ZONE = 'Asia/Kolkata';

/** The customer's own timezone, as their browser reports it. IST when unknowable. */
export function customerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || IST_ZONE;
  } catch {
    return IST_ZONE;
  }
}

/**
 * One slot instant, in one timezone: "Mon, 15 Sep, 12:30".
 *
 * Returns null (never "Invalid Date") when the value does not parse — a wire
 * value we cannot read is the caller's problem to degrade honestly, not ours to
 * render as a broken time at a customer.
 */
export function formatSlotStamp(iso: string, locale: string, timeZone: string): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(intlLocaleOf(locale), {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    }).format(at);
  } catch {
    // An unknown timezone/locale from the environment — fall back to the zone we
    // know is valid rather than losing the appointment time entirely.
    try {
      return new Intl.DateTimeFormat(intlLocaleOf(locale), {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: IST_ZONE,
      }).format(at);
    } catch {
      return null;
    }
  }
}

/** The customer-local line, plus the IST line when it would actually say something new. */
export interface SlotTimeLines {
  /** The slot in the customer's own timezone — the time they act on. */
  local: string;
  /** The same slot in IST, or null when the local line already IS the IST line. */
  ist: string | null;
}

export function slotTimeLines(
  iso: string,
  locale: string,
  timeZone: string,
): SlotTimeLines | null {
  const local = formatSlotStamp(iso, locale, timeZone);
  if (local === null) return null;
  const ist = formatSlotStamp(iso, locale, IST_ZONE);
  // Compared by RENDERING, not by zone id: `Asia/Calcutta`, `Asia/Kolkata` and any
  // future alias all collapse correctly, and no customer sees the same clock time
  // printed twice.
  return { local, ist: ist === null || ist === local ? null : ist };
}
